import React from 'react';
import PropTypes from 'prop-types';
import {Box, IconButton, Stack, Typography} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import {
  ERROR_100,
  SUCCESS_100,
  WARNING_100,
  INFO_100,
  WHITE_100,
} from '../../theme';

const statusConfig = {
  success: {
    color: SUCCESS_100,
    icon: CheckCircleOutlineIcon,
    iconSize: 24,
    showSubtitle: false,
  },
  error: {
    color: ERROR_100,
    icon: InfoOutlinedIcon,
    iconSize: 32,
    showSubtitle: true,
  },
  warning: {
    color: WARNING_100,
    icon: InfoOutlinedIcon,
    iconSize: 32,
    showSubtitle: true,
  },
  information: {
    color: INFO_100,
    icon: InfoOutlinedIcon,
    iconSize: 32,
    showSubtitle: true,
  },
};

const styles = {
  container: {
    borderRadius: '12px',
    padding: '16px 24px',
    boxShadow: '0px 2px 12px 0px rgba(0, 0, 0, 0.12)',
  },
  title: {
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: 1.3,
    letterSpacing: '-0.14px',
    color: WHITE_100,
  },
  subtitle: {
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: 1.4,
    letterSpacing: '-0.12px',
    color: '#F2F4F4',
  },
  closeButton: {
    color: WHITE_100,
    padding: 0,
    '&:hover': {backgroundColor: 'transparent'},
  },
};

const Notification = React.forwardRef(function Notification(
  {status, title, subtitle, onClose, icon},
  ref
) {
  const config = statusConfig[status] || statusConfig.information;
  const IconComponent = icon || config.icon;

  return (
    <Box
      ref={ref}
      sx={{
        ...styles.container,
        backgroundColor: config.color,
        display: 'flex',
        alignItems: 'center',
        gap: config.showSubtitle ? '24px' : '12px',
        minWidth: config.showSubtitle ? '300px' : '200px',
      }}
    >
      <Stack direction="row" alignItems="center" gap="12px" sx={{flex: 1}}>
        <IconComponent sx={{color: WHITE_100, fontSize: config.iconSize}} />
        {config.showSubtitle ? (
          <Stack direction="column" gap="4px">
            <Typography sx={styles.title}>{title}</Typography>
            {subtitle && (
              <Typography sx={styles.subtitle}>{subtitle}</Typography>
            )}
          </Stack>
        ) : (
          <Typography sx={styles.title}>{title}</Typography>
        )}
      </Stack>
      {onClose && (
        <IconButton onClick={onClose} sx={styles.closeButton}>
          <CloseIcon sx={{fontSize: 24}} />
        </IconButton>
      )}
    </Box>
  );
});

Notification.propTypes = {
  status: PropTypes.oneOf(['success', 'error', 'warning', 'information'])
    .isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  onClose: PropTypes.func,
  icon: PropTypes.elementType,
};

export default Notification;
